import algoliasearch from 'algoliasearch/lite';

export const FILE_TYPES = new Map([
  ['application/msword', 'doc'],
  ['application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'docx'],
  ['application/vnd.ms-powerpoint', 'ppt'],
  ['application/vnd.openxmlformats-officedocument.presentationml.presentation', 'pptx'],
  ['application/vnd.ms-excel', 'xls'],
  ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'xlsx'],
  ['application/pdf', 'pdf'],
  ['image/png', 'png'],
  ['image/gif', 'gif'],
  ['image/jpeg', 'jpg'],
  ['image/jpeg', 'jpeg'],
  ['text/plain', 'txt'],
  ['application/dca-rft', 'rft'],
  ['image/svg+xml', 'svg'],
  ['audio/mpeg', 'mp3'],
  ['video/mp4', 'mp4'],
  ['application/epub+zip', 'epub'],
  ['model/vnd.collada+xml', 'dae'],
  ['image/vnd.dwg', 'dwg'],
  ['application/x-koan', 'skp'],
  ['application/vnd.ms-pki.stl', 'stl'],
  ['text/csv', 'csv'],
]);

export const PARAGRAPH_TRANSLATIONS = new Map([
  ['Pages', 'Ngā Whārangi'],
  ['Video', 'Ngā Kiriata'],
  ['Image', 'Ngā Whakaahua'],
  ['Audio', 'Ngā Oro'],
  ['Downloads', 'Ngā Tikinga Ake'],
]);

/**
 * Cache data in localstorage with an expiry time.
 * @param {string} key localstorage key
 * @param {any} value data to cache
 * @param {number} ttl time to live in days
 */
function setWithExpiry(key, value, ttl) {
  const item = {
    value,
    expiry: new Date().getTime() + 1000 * 60 * 60 * 24 * ttl,
  };
  localStorage.setItem(key, JSON.stringify(item));
}

function getWithExpiry(key) {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) return null;

  const item = JSON.parse(itemStr);
  // If the item is expired, delete item and return null.
  if (new Date() > item.expiry) {
    localStorage.removeItem(key);
    return null;
  }
  return item.value;
}

/**
 * Loads all translations into local storage if not exists or if expired.
 * @param translations JSON object containing label translation keypairs.
 */
export const loadTermTranslations = (translations) => {
  if (getWithExpiry('term_translations') !== null) return;
  setWithExpiry('term_translations', translations, 1);
};

/**
 * Translates input using term translation data.
 * Uses the same logic as `TermTranslation` Twig extension
 * @param {string} term
 * @param {boolean} showEn
 * @param {int} index
 * @returns translated term
 */
export const trans = (term, showEn, index = null) => {
  const data = JSON.parse(window.localStorage.getItem('term_translations'))?.value?.mi;
  if (showEn || !data) return term;

  const miTerm = index !== null ? data[term][index] : data[term];
  return miTerm ?? term;
};

/**
 * pickTextColorBasedOnBgColor
 * ===========================
 */
const pickTextColorBasedOnBgColor = (bgColor, lightColor, darkColor) => {
  const color = bgColor.charAt(0) === '#' ? bgColor.substring(1, 7) : bgColor;
  const r = parseInt(color.substring(0, 2), 16); // hexToR
  const g = parseInt(color.substring(2, 4), 16); // hexToG
  const b = parseInt(color.substring(4, 6), 16); // hexToB

  return r * 0.299 + g * 0.587 + b * 0.114 > 130 ? darkColor : lightColor;
};

/**
 * isMobileViewPort
 * ================
 */
export const isMobileViewPort = (mobileBreakpoint = 1120) => {
  const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
  return vw <= mobileBreakpoint;
};

/**
 * toggleBlockMenuById
 * ===================
 * Toggles menu block by block id.
 */
export const toggleBlockMenusById = (menuIds, toggleClassName = 'block-menu--expanded') => {
  menuIds.map((menuId) => document.getElementById(menuId)?.classList.toggle(toggleClassName));
};

/**
 * toggleCurriculumListById
 * ========================
 * Toggles curriculum's subjects lists by id.
 * @param menusId Array of ID's for elements to be toggled.
 * @param toggleClassName Allow override of toggle class name.
 */
export const toggleCurriculumListById = (
  menuIds,
  toggleClassName = 'menu-item-1-list--expanded'
) => {
  menuIds.map((menuId) => document.getElementById(menuId)?.classList.toggle(toggleClassName));
};

/**
 * getBgColour
 * ===========
 */
export const getBgColour = (color) => {
  if (color !== null) {
    return { 'background-color': `${color}` };
  }
  return '';
};

/**
 * getBgImage
 * ==========
 */
export const getBgImage = (imageUri) => {
  if (imageUri) {
    return { 'background-image': `url(${imageUri})` };
  }
  return '';
};

/**
 * getBorderColour
 * ===============
 */
export const getBorderColour = (color) => {
  if (color !== null) {
    return { 'border-color': `${color}` };
  }
  return '';
};

/**
 * getBorderStyle
 * ==============
 */
export const getBorderStyle = (bgColour) => ({
  'border-top': `2px solid ${bgColour}`,
  color: bgColour,
});

/**
 * getTextColour
 * =============
 */
export const getTextColour = (textColour, secondaryColor = null) => {
  if (secondaryColor !== null && isMobileViewPort()) {
    return {
      color: pickTextColorBasedOnBgColor(secondaryColor, '#FFFFFF', '#000000'),
    };
  }

  return {
    color: pickTextColorBasedOnBgColor(textColour, '#FFFFFF', '#000000'),
  };
};

/**
 * setBackgroundColour
 * ===================
 */
export const setBackgroundColour = (bgColour) => ({
  background: bgColour,
});

/**
 * setTextColour
 * =============
 */
export const setTextColour = (color) => ({
  color: `${color}`,
});

/**
 * amendLanguageLinkViewParam
 * ==========================
 * Amends `?view=<tabId>` part of language toggle link.
 * This is required for legacy content types (Subject, Achievement Standard, etc) that don't
 * follow the new architecture.
 * @param activeTab Tab id param to be set in url "~/mi/arts/visual-arts/1/1?view=unpacking".
 */
export const amendLanguageLinkViewParam = (activeTab, anchor = '') => {
  const languageLinks = document.getElementsByClassName('language-link');
  Array.prototype.forEach.call(languageLinks, (languageLink) => {
    if (
      languageLink.href &&
      languageLink.href.length > 0 &&
      !languageLink.classList.contains('is-active')
    ) {
      languageLink.setAttribute('href', `${languageLink.href}?view=${activeTab}${anchor}`);
    }
  });
};

/**
 * formatBytes
 * ===========
 * Helper function to convert bytes into appropriate unit.
 * @param array $bytes Size of file in bytes.
 */
export const formatBytes = (bytes, rounding = 2) => {
  const kilobyte = 1024;
  const megabyte = kilobyte * 1024;
  const gigabyte = megabyte * 1024;
  const terabyte = gigabyte * 1024;

  let result = '';
  let unit = '';

  if (bytes < kilobyte) {
    result = bytes;
    unit = 'B';
  } else if (bytes < megabyte) {
    result = bytes / kilobyte;
    unit = 'KB';
  } else if (bytes < gigabyte) {
    result = bytes / megabyte;
    unit = 'MB';
  } else if (bytes < terabyte) {
    result = bytes / gigabyte;
    unit = 'GB';
  } else {
    result = bytes / terabyte;
    unit = 'TB';
  }

  // Round result to 2 decimal points.
  result = result.toFixed(rounding);
  result = `${result} ${unit}`;

  return result;
};

export const algoliaClient = algoliasearch('C6I38FR7ME', '601f1008457716a9daa31f85bddc3970');

export const searchClient = {
  ...algoliaClient,
  search(requests) {
    const newRequests = requests.map((request) => {
      const { query } = request.params;
      // test for empty string and change request parameter: analytics
      if (!query || query.length === 0 || query === '*') {
        request.params.analytics = false;
      }
      return request;
    });
    return algoliaClient.search(newRequests);
  },
};
